import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import { BookOpen, Filter, ExternalLink, Tag, Sparkles } from 'lucide-react';

const CATEGORIES = [
  { value: 'all', label: 'All Resources' },
  { value: 'beginner-friendly', label: 'Beginner Friendly' },
  { value: 'tools', label: 'Tools' },
  { value: 'tutorials', label: 'Tutorials' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'general-knowledge', label: 'General Knowledge' }
];

const RESOURCE_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'article', label: 'Article' },
  { value: 'video', label: 'Video' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'course', label: 'Course' },
  { value: 'tool', label: 'Tool' },
  { value: 'repository', label: 'Repository' },
  { value: 'other', label: 'Other' }
];

const LearningResources = () => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false

    const fetchResources = async () => {
      try {
        const response = await axios.get('/public/resources');
        if (cancelled) return
        setResources(response.data);
        setLoading(false);
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching resources:', error);
        setLoading(false);
      }
    };

    fetchResources()
    return () => { cancelled = true }
  }, []);

  useEffect(() => {
    filterResources();
  }, [selectedCategory, selectedType, resources]);

  const filterResources = () => {
    let filtered = [...resources];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(r => r.category === selectedCategory);
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter(r => r.type === selectedType);
    }

    setFilteredResources(filtered);
  };

  const getTypeColor = (type) => {
    const colors = {
      article: 'bg-blue-100 text-blue-700',
      video: 'bg-red-100 text-red-700',
      documentation: 'bg-green-100 text-green-700',
      course: 'bg-purple-100 text-purple-700',
      tool: 'bg-yellow-100 text-yellow-700',
      repository: 'bg-gray-100 text-gray-700',
      other: 'bg-pink-100 text-pink-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'beginner-friendly': 'Beginner Friendly',
      'tools': 'Tools',
      'tutorials': 'Tutorials',
      'advanced': 'Advanced',
      'general-knowledge': 'General Knowledge'
    };
    return labels[category] || category;
  };

  // Separate beginner-friendly resources
  const beginnerResources = filteredResources.filter(r => r.isBeginnerFriendly);
  const otherResources = filteredResources.filter(r => !r.isBeginnerFriendly);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Learning Resources</h1>
          </div>
          <p className="text-muted-foreground">Curated resources to help you learn and grow as an open source contributor</p>
        </div>

        {/* Filters */}
        <div className="bg-card rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-card-foreground">Filter Resources</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {RESOURCE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredResources.length} resource{filteredResources.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Beginner-Friendly Resources */}
        {beginnerResources.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Perfect for Beginners</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {beginnerResources.map(resource => (
                <ResourceCard key={resource.id} resource={resource} getTypeColor={getTypeColor} getCategoryLabel={getCategoryLabel} highlighted />
              ))}
            </div>
          </div>
        )}

        {/* Other Resources */}
        {otherResources.length > 0 && (
          <div>
            {beginnerResources.length > 0 && (
              <h2 className="text-2xl font-bold text-foreground mb-4">More Resources</h2>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherResources.map(resource => (
                <ResourceCard key={resource.id} resource={resource} getTypeColor={getTypeColor} getCategoryLabel={getCategoryLabel} />
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {filteredResources.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No resources found</h3>
            <p className="text-muted-foreground">Try adjusting your filters to see more results</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ResourceCard = ({ resource, getTypeColor, getCategoryLabel, highlighted = false }) => {
  return (
    <div className={`bg-card rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden ${highlighted ? 'border-2 border-primary' : 'border border-border'}`}>
      <div className="p-6">
        {highlighted && (
          <div className="flex items-center gap-1 text-primary text-sm font-medium mb-2">
            <Sparkles className="w-4 h-4" />
            Beginner Friendly
          </div>
        )}
        
        <h3 className="text-lg font-semibold text-card-foreground mb-2 line-clamp-2">
          {resource.name}
        </h3>
        
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {resource.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(resource.type)}`}>
            {resource.type}
          </span>
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 flex items-center gap-1">
            <Tag className="w-3 h-3" />
            {getCategoryLabel(resource.category)}
          </span>
        </div>

        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-primary hover:opacity-80 font-medium text-sm transition-colors"
        >
          Visit Resource
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

export default LearningResources;
